import {
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import { canAccessModule } from "../lib/access";
import { ACTIVE_PAGE_REFRESH_INTERVAL_MS } from "../lib/activePageRefresh";
import { type AssetId, assetIdSchema, workOrderIdSchema } from "../lib/workIds";

const lookup = z.object({ code: z.string(), name: z.string() });
const person = z.object({
  id: z.number(),
  name: z.string(),
  initials: z.string(),
});
/**
 * Server-computed per-action availability (IWA-19230) so Web never infers
 * permissions from stale local role state — only from the latest API
 * response.
 */
const workOrderActions = z.object({
  REOPEN: z.boolean(),
  // Defaults to false so an older API response hides the attach control.
  ATTACH_PROCEDURE: z.boolean().default(false),
  EDIT: z.boolean(),
  ASSIGN: z.boolean(),
  TRANSITION: z.boolean(),
  COMPLETE: z.boolean(),
  CANCEL: z.boolean(),
  DELETE: z.boolean(),
  RESTORE: z.boolean(),
  // Defaults to false so an older API response hides the self-assign control.
  CLAIM: z.boolean().default(false),
});
const workOrder = z.object({
  id: workOrderIdSchema,
  version: z.string().regex(/^[1-9]\d{0,9}$/),
  number: z.string(),
  title: z.string(),
  description: z.string().nullable().optional().default(null),
  instructions: z.string().nullable().optional().default(null),
  cancellationReason: z.string().nullable(),
  status: lookup,
  priority: lookup.nullable(),
  workType: lookup,
  plannerGroup: lookup.nullable(),
  assignee: person.nullable(),
  assignees: z.array(person).default([]),
  category: z.string().nullable(),
  asset: z
    .object({ id: assetIdSchema, code: z.string(), name: z.string() })
    .nullable(),
  location: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdOn: z.string().datetime().nullable(),
  updatedOn: z.string().datetime().nullable(),
  dueDate: z.string().datetime().nullable(),
  completedOn: z.string().datetime().nullable(),
  completedBy: z.string().nullable(),
  basicStartDate: z.string().datetime().nullable(),
  basicFinishDate: z.string().datetime().nullable(),
  totalCost: z.number(),
  canComplete: z.boolean(),
  // Defaults to false so an older API response shows no unread marker.
  isUnread: z.boolean().default(false),
  actions: workOrderActions,
  statusTargets: z.array(z.string()).default([]),
});
const responseSchema = z.object({
  workOrders: z.array(workOrder),
  page: z.number(),
  limit: z.number(),
  totalCount: z.number(),
  totalCost: z.number(),
  metadata: z.object({
    counts: z.object({
      all: z.number(),
      mine: z.number(),
      open: z.number().default(0),
      ready: z.number(),
      created: z.number().default(0),
      done: z.number().default(0),
      teamQueue: z.number(),
    }),
    priorities: z.array(lookup),
    statuses: z.array(lookup),
    workTypes: z.array(lookup),
    plannerGroups: z.array(lookup),
    assets: z.array(lookup),
    categories: z.array(lookup),
    assignees: z.array(lookup),
    completedByOptions: z.array(lookup),
    createdByOptions: z.array(lookup),
  }),
});
export type WorkOrder = z.infer<typeof workOrder>;
export type WorkOrderView =
  | "all"
  | "mine"
  | "created"
  | "open"
  | "done"
  | "ready"
  | "team-queue";
export type WorkOrdersFilters = {
  assetId?: AssetId;
  enabled?: boolean;
  view: WorkOrderView;
  page: number;
  limit?: number;
  search: string;
  priorities: string[];
  statuses: string[];
  workTypes: string[];
  assetIds: string[];
  categoryIds: string[];
  assignees: string[];
  dueDateFrom?: string;
  dueDateTo?: string;
  sort?: "number" | "title" | "status" | "priority" | "updated";
  direction?: "asc" | "desc";
};
export type WorkOrdersResponse = z.infer<typeof responseSchema>;
async function fetchWorkOrders(
  customerId: string,
  facilityId: string,
  filters: WorkOrdersFilters,
) {
  const response = await workClient.v1.customers[":customerId"].facilities[
    ":facilityId"
  ]["work-orders"].$get({
    param: { customerId, facilityId },
    query: {
      ...(filters.assetId ? { assetId: filters.assetId } : {}),
      page: String(filters.page),
      limit: String(filters.limit ?? 25),
      view: filters.view,
      ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
      ...(filters.priorities.length
        ? { priorities: filters.priorities.join(",") }
        : {}),
      ...(filters.statuses.length
        ? { statuses: filters.statuses.join(",") }
        : {}),
      ...(filters.workTypes.length
        ? { workTypes: filters.workTypes.join(",") }
        : {}),
      ...(filters.assetIds.length
        ? { assetIds: filters.assetIds.join(",") }
        : {}),
      ...(filters.categoryIds.length
        ? { categoryIds: filters.categoryIds.join(",") }
        : {}),
      ...(filters.assignees.length
        ? { assignees: filters.assignees.join(",") }
        : {}),
      ...(filters.dueDateFrom ? { dueDateFrom: filters.dueDateFrom } : {}),
      ...(filters.dueDateTo ? { dueDateTo: filters.dueDateTo } : {}),
      ...(filters.sort ? { sort: filters.sort } : {}),
      ...(filters.direction ? { direction: filters.direction } : {}),
    },
  });
  if (!response.ok)
    throw new Error(`Failed to load Work Orders: ${response.status}`);
  return responseSchema.parse(await response.json());
}
export function useWorkOrdersQuery(filters: WorkOrdersFilters) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const pageFilters = { ...filters, limit: 25, page: 1 };
  const query = useInfiniteQuery({
    queryKey: [
      "work-orders",
      "pages",
      selectedCustomer?.id,
      selectedFacility?.id,
      selectedFacility?.role,
      pageFilters,
    ],
    queryFn: ({ pageParam }) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      return fetchWorkOrders(selectedCustomer.id, selectedFacility.id, {
        ...pageFilters,
        page: pageParam,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.limit < lastPage.totalCount
        ? lastPage.page + 1
        : undefined,
    enabled:
      Boolean(selectedCustomer && selectedFacility) &&
      (filters.enabled ?? true),
    placeholderData: keepPreviousData,
    refetchInterval: ACTIVE_PAGE_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    select: (data) => {
      const visiblePages = data.pages.slice(0, filters.page);
      return {
        ...visiblePages[0],
        workOrders: visiblePages.flatMap((page) => page.workOrders),
        totalCost: visiblePages.reduce(
          (total, page) => total + page.totalCost,
          0,
        ),
        pageCount: data.pages.length,
      };
    },
    meta: { errorMessage: "Failed to load Work Orders." },
  });
  return {
    ...query,
    hasNextPage:
      query.hasNextPage || (query.data?.pageCount ?? 0) > filters.page,
  };
}

export function workOrdersQueryOptions({
  customerId,
  facilityId,
  facilityRole,
  filters,
}: {
  customerId?: string;
  facilityId?: string;
  facilityRole?: string;
  filters: WorkOrdersFilters;
}) {
  return queryOptions({
    queryKey: ["work-orders", customerId, facilityId, facilityRole, filters],
    queryFn: () => {
      if (!customerId || !facilityId)
        throw new Error("A customer and facility must be selected.");
      return fetchWorkOrders(customerId, facilityId, filters);
    },
    enabled: Boolean(customerId && facilityId) && (filters.enabled ?? true),
    placeholderData: keepPreviousData,
    refetchInterval: ACTIVE_PAGE_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    meta: { errorMessage: "Failed to load Work Orders." },
  });
}

export function useAssetWorkOrderHistoryQuery(assetId: AssetId | null) {
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useQuery({
    queryKey: [
      "work-order-history",
      selectedCustomer?.id,
      selectedFacility?.id,
      selectedFacility?.role,
      assetId,
    ],
    queryFn: async () => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }
      if (!assetId) {
        throw new Error("An asset must be selected.");
      }

      const filters = {
        assetId,
        direction: "desc" as const,
        limit: 100,
        page: 1,
        priorities: [],
        search: "",
        sort: "updated" as const,
        statuses: [],
        view: "all" as const,
        workTypes: [],
        assetIds: [],
        categoryIds: [],
        assignees: [],
      };
      const firstPage = await fetchWorkOrders(
        selectedCustomer.id,
        selectedFacility.id,
        filters,
      );
      const pageCount = Math.ceil(
        firstPage.totalCount / Math.max(firstPage.limit, 1),
      );
      if (pageCount <= 1) return firstPage;

      const remainingPages = await Promise.all(
        Array.from({ length: pageCount - 1 }, (_, index) =>
          fetchWorkOrders(selectedCustomer.id, selectedFacility.id, {
            ...filters,
            page: index + 2,
          }),
        ),
      );

      return {
        ...firstPage,
        totalCost: [firstPage, ...remainingPages].reduce(
          (total, page) => total + page.totalCost,
          0,
        ),
        workOrders: [
          firstPage.workOrders,
          ...remainingPages.map((page) => page.workOrders),
        ].flat(),
      };
    },
    enabled:
      Boolean(selectedCustomer && selectedFacility) &&
      Boolean(
        selectedFacility && canAccessModule(selectedFacility, "WORK_ORDERS"),
      ) &&
      assetId !== null,
    meta: { errorMessage: "Failed to load asset Work Order history." },
  });
}
